import Keycloak from "keycloak-js"

import { setAccountInfo } from "./store/reducers/accountinfo"

// Create the Keycloak client from env config (URL, realm, client ID)
const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL,
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
})

let initPromise = null     // holds the init() promise so we only init once
let refreshTimeout = null  // handle for the pending token-refresh timer

// Initialize Keycloak once and reuse the same promise for later calls.
// Keycloak can only be initialized once, so every caller awaits this shared promise.
const initKeycloak = (options) => {
  if (!initPromise) {
    initPromise = keycloak.init({
      pkceMethod: "S256",       // secure auth flow for public SPA clients
      checkLoginIframe: false,  // avoids third-party-cookie session-check bugs
      ...options,
    })
  }
  return initPromise
}

// Push the current user info + token from Keycloak into Redux
const updateAccountInfo = (dispatch) => {
  dispatch(setAccountInfo({
    name: keycloak.tokenParsed.name,
    email: keycloak.tokenParsed.email,
    email_verified: keycloak.tokenParsed.email_verified,
    token: keycloak.token,
    roles: keycloak.realmAccess?.roles || []
  }))
}

const REFRESH_MARGIN = 30 // refresh the token this many seconds before it expires

// Schedule an automatic token refresh shortly before the token expires,
// then reschedule itself so the user stays logged in indefinitely.
const scheduleTokenRefresh = (dispatch) => {
  // Cancel any refresh already scheduled (avoids overlapping timers)
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }

  // Seconds left on the current token
  const expiresIn = keycloak.tokenParsed.exp - Math.ceil(Date.now() / 1000)
  // Wait until ~REFRESH_MARGIN before expiry, but never schedule a 0ms timer
  const refreshIn = Math.max((expiresIn - REFRESH_MARGIN) * 1000, 5000)

  refreshTimeout = setTimeout(() => {
    // minValidity must be > REFRESH_MARGIN, otherwise the token still has
    // exactly REFRESH_MARGIN seconds left and updateToken would skip the refresh
    keycloak.updateToken(REFRESH_MARGIN + 30)
      .then((refreshed) => {
        if (refreshed) updateAccountInfo(dispatch) // token changed → update store
        scheduleTokenRefresh(dispatch)             // queue the next refresh
      })
      .catch(() => {
        // Refresh failed (e.g. session expired) → send user to login
        console.warn("Token refresh failed, redirecting to login")
        keycloak.login()
      })
  }, refreshIn)
}

// 1. Silent check on mount - detects an existing session without redirecting
export const checkSSOSession = () => async (dispatch) => {
  try {
    const authenticated = await initKeycloak({
      onLoad: "check-sso", // check silently, don't force a login
      silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
    })

    if (!authenticated) return false // no session → caller decides what to do

    updateAccountInfo(dispatch)
    scheduleTokenRefresh(dispatch)

    return true
  } catch (error) {
    console.warn("SSO check error", error)
    return false
  }
}

// 2. Explicit login - redirects to the Keycloak login page if there's no session
export const getAccessToken = () => async (dispatch) => {
  try {
    const authenticated = await initKeycloak({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
    })

    if (!authenticated) {
      return keycloak.login() // full page redirect - code after this won't run
    }

    updateAccountInfo(dispatch)
    scheduleTokenRefresh(dispatch)

    return keycloak.token
  } catch (error) {
    console.warn("Login error", error)
  }
}

// Log out: stop the refresh timer and redirect through Keycloak's logout
export const logout = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }

  keycloak.logout({
    redirectUri: window.location.href // return here after logout
  })
}